import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { toast } from 'react-toastify';

const initialState = {
  loading: false,
  error: null,
  createdCampaign: null,
};

export const createAdminCampaign = createAsyncThunk(
  'adminCampaign/create',
  async (campaignData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URI}/artistCampaign/adminCreateCampaign`,
        campaignData,
        {
          headers: {
            'Content-Type': 'application/json',
            authorization: token,
          },
        }
      );
      toast.success('Campaign created successfully');
      return response.data.data;
    } catch (error) {
      const errorMsg =
        error.response?.data?.message || 'Failed to create campaign';
      toast.error(errorMsg);
      return rejectWithValue(error.response?.data);
    }
  }
);

export const uploadPaymentDocuments = createAsyncThunk(
  'adminCampaign/uploadDocuments',
  async ({ campaignId, files }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('access_token');
      const formData = new FormData();
      files.forEach(file => {
        formData.append('documents', file);
      });
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URI}/artistCampaign/${campaignId}/payment-documents`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            authorization: token,
          },
        }
      );
      toast.success('Documents uploaded successfully');
      return response.data.data;
    } catch (error) {
      const errorMsg =
        error.response?.data?.message || 'Failed to upload documents';
      toast.error(errorMsg);
      return rejectWithValue(error.response?.data);
    }
  }
);

export const convertCampaignToEur = createAsyncThunk(
  'adminCampaign/convertToEur',
  async ({ campaignId, exchangeRate }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URI}/artistCampaign/${campaignId}/convert-to-eur`,
        {
          targetCurrencyCode: 'EUR',
          exchangeRate,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            authorization: token,
          },
        }
      );
      toast.success('Campaign converted to EUR successfully');
      return response.data.data;
    } catch (error) {
      const errorMsg =
        error.response?.data?.message || 'Failed to convert campaign to EUR';
      toast.error(errorMsg);
      return rejectWithValue(error.response?.data);
    }
  }
);

export const updateCampaignInternalPricing = createAsyncThunk(
  'adminCampaign/updateInternalPricing',
  async ({ campaignId, internalBudgetRatePer1k, internalBudgetTotal }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('access_token');
      const payload = {};

      if (internalBudgetRatePer1k !== undefined) {
        payload.internalBudgetRatePer1k = internalBudgetRatePer1k;
      }

      if (internalBudgetTotal !== undefined) {
        payload.internalBudgetTotal = internalBudgetTotal;
      }

      const response = await axios.patch(
        `${process.env.REACT_APP_BACKEND_URI}/artistCampaign/${campaignId}/internal-pricing`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            authorization: token,
          },
        }
      );
      toast.success('Campaign internal pricing updated successfully');
      return response.data.data;
    } catch (error) {
      const errorMsg =
        error.response?.data?.message || 'Failed to update campaign internal pricing';
      toast.error(errorMsg);
      return rejectWithValue(error.response?.data);
    }
  }
);

export const updateAdminCampaignBudgetAndCurrency = createAsyncThunk(
  'adminCampaign/updateBudgetAndCurrency',
  async ({ campaignId, budget, currencyCode }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.patch(
        `${process.env.REACT_APP_BACKEND_URI}/artistCampaign/${campaignId}/budget-currency`,
        {
          budget,
          currencyCode,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            authorization: token,
          },
        }
      );
      toast.success('Campaign amount and currency updated successfully');
      return response.data.data;
    } catch (error) {
      const errorMsg =
        error.response?.data?.message || 'Failed to update campaign amount and currency';
      toast.error(errorMsg);
      return rejectWithValue(error.response?.data);
    }
  }
);

export const getPaymentDocuments = createAsyncThunk(
  'adminCampaign/getDocuments',
  async ({ campaignId }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URI}/artistCampaign/${campaignId}/payment-documents`,
        {
          headers: {
            authorization: token,
          },
        }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const removePaymentDocument = createAsyncThunk(
  'adminCampaign/removeDocument',
  async ({ campaignId, documentId }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URI}/artistCampaign/${campaignId}/payment-documents/${documentId}`,
        {
          headers: {
            authorization: token,
          },
        }
      );
      toast.success('Document removed successfully');
      return response.data.data;
    } catch (error) {
      const errorMsg =
        error.response?.data?.message || 'Failed to remove document';
      toast.error(errorMsg);
      return rejectWithValue(error.response?.data);
    }
  }
);

export const adminCampaignSlice = createSlice({
  name: 'adminCampaignSlice',
  initialState,
  reducers: {
    resetAdminCampaignState: (state) => {
      state.loading = false;
      state.error = null;
      state.createdCampaign = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createAdminCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAdminCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.createdCampaign = action.payload;
      })
      .addCase(createAdminCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetAdminCampaignState } = adminCampaignSlice.actions;
export default adminCampaignSlice.reducer;
